import { postAnalyticsEvent, type AnalyticsEventPayload } from '../api/analytics';
import type { eventoSeleccionado } from '../types/types';

export type EventAnalyticsStep =
  | 'PageView'
  | 'AddToCart'
  | 'InitiateCheckout'
  | 'Purchase';

export type EventAnalyticsConfig = {
  pixelFacebook1?: string | null;
  pixelFacebook2?: string | null;
  ga4Code?: string | null;
};

export const EMPTY_EVENT_ANALYTICS_CONFIG: EventAnalyticsConfig = {
  pixelFacebook1: null,
  pixelFacebook2: null,
  ga4Code: null,
};

export function buildEventAnalyticsConfig(
  evento: Pick<eventoSeleccionado, 'pixelFacebook1' | 'pixelFacebook2' | 'ga4Code'>
): EventAnalyticsConfig {
  return {
    pixelFacebook1: evento.pixelFacebook1 ?? null,
    pixelFacebook2: evento.pixelFacebook2 ?? null,
    ga4Code: evento.ga4Code ?? null,
  };
}

export function matchesEventHash(
  evento: eventoSeleccionado | null | undefined,
  hashEvent?: string
): boolean {
  return !!(evento && hashEvent && evento.eventHash === hashEvent);
}

const GA4_EVENT_MAP: Record<EventAnalyticsStep, string> = {
  PageView: 'page_view',
  AddToCart: 'add_to_cart',
  InitiateCheckout: 'begin_checkout',
  Purchase: 'purchase',
};

const BACKEND_EVENT_MAP = {
  PageView: 'paso_1',
  AddToCart: 'paso_2',
  InitiateCheckout: 'paso_3',
  Purchase: 'paso_4',
} as const satisfies Record<EventAnalyticsStep, AnalyticsEventPayload['eventType']>;

function buildAnalyticsPayload(
  step: EventAnalyticsStep,
  eventHash?: string,
  backendParams?: Record<string, unknown>,
  params?: Record<string, unknown>
): AnalyticsEventPayload {
  const eventType = BACKEND_EVENT_MAP[step];
  const payload: AnalyticsEventPayload = {
    eventType,
    params: backendParams ?? params ?? {},
  };

  if (step === 'PageView' && eventHash) {
    payload.generalSellableVariantId = eventHash;
  }

  return payload;
}

declare global {
  interface Window {
    loadFacebookPixel: (pixelId: string, slot?: number) => void;
    unloadFacebookPixelSlot: (slot: number) => void;
    trackFacebookPixel: (eventName: string, params?: Record<string, unknown>) => void;
    loadGa4: (measurementId: string) => void;
    unloadGa4: () => void;
    trackGa4: (eventName: string, params?: Record<string, unknown>) => void;
  }
}

window.loadFacebookPixel = window.loadFacebookPixel || (() => {});
window.unloadFacebookPixelSlot = window.unloadFacebookPixelSlot || (() => {});
window.trackFacebookPixel = window.trackFacebookPixel || (() => {});
window.loadGa4 = window.loadGa4 || (() => {});
window.unloadGa4 = window.unloadGa4 || (() => {});
window.trackGa4 = window.trackGa4 || (() => {});

export function initEventAnalytics(config: EventAnalyticsConfig): void {
  if (config.ga4Code) {
    window.loadGa4(config.ga4Code);
  } else {
    window.unloadGa4();
  }

  if (config.pixelFacebook1) {
    window.loadFacebookPixel(config.pixelFacebook1, 1);
  } else {
    window.unloadFacebookPixelSlot(1);
  }

  if (config.pixelFacebook2) {
    window.loadFacebookPixel(config.pixelFacebook2, 2);
  } else {
    window.unloadFacebookPixelSlot(2);
  }
}

export function trackEventAnalytics(
  config: EventAnalyticsConfig,
  step: EventAnalyticsStep,
  params?: Record<string, unknown>,
  backendParams?: Record<string, unknown>,
  eventHash?: string
): void {
  initEventAnalytics(config);

  const hasTracking = !!(config.pixelFacebook1 || config.pixelFacebook2 || config.ga4Code);

  if (hasTracking) {
    if (config.pixelFacebook1 || config.pixelFacebook2) {
      window.trackFacebookPixel(step, params);
    }

    if (config.ga4Code) {
      window.trackGa4(GA4_EVENT_MAP[step], params);
    }
  }

  void postAnalyticsEvent(buildAnalyticsPayload(step, eventHash, backendParams, params)).catch((error) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[analytics] post failed:', error?.response?.status, error?.response?.data ?? error);
    }
  });
}
